import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { formatBDT } from '@maktech/shared';
import { api, errorMessage } from '../../api/client';
import { Modal } from '../../components/Modal';
import { useCart } from './CartContext';
import type { Quote } from './CartModal';

/** Payment methods — spec 7.4. */
/**
 * SPEC/PROTOTYPE CONFLICT — REPORTED, spec followed. See the note to the client.
 *
 * The prototype's checkout offers FOUR methods; its wiring map (:1876) is
 * literally {"💳 Card":"card","bKash":"bkash","Nagad":"nagad","PayPal":"paypal"}
 * — there is no Rocket chip. Spec 7.4 says "bKash / Nagad / Rocket / card /
 * PayPal", five. The rule is that the prototype wins, which would mean dropping
 * Rocket; the reason it is kept is that the prototype CONTRADICTS ITSELF rather
 * than disagreeing with the spec: its own trust badge (:2197) lists Rocket, and
 * its own payment-help page (:4065) says "bKash, Nagad, Rocket, card and PayPal
 * — step by step". Shipping the 4-chip version would advertise a wallet at
 * checkout that the checkout refuses to take. Labels/order follow the prototype.
 */
const METHODS: { id: 'bkash' | 'nagad' | 'rocket' | 'card' | 'paypal'; label: string }[] = [
  { id: 'card', label: '💳 Card' },
  { id: 'bkash', label: 'bKash' },
  { id: 'nagad', label: 'Nagad' },
  { id: 'rocket', label: 'Rocket' },
  { id: 'paypal', label: 'PayPal' },
];

/**
 * Checkout — completePurchase() (:2268 base + :5234 v13 wrapper).
 *
 * No payment provider is wired yet (SSLCommerz is a Phase 10 integration), so
 * this records a paid order directly, exactly as the prototype did. The
 * difference is that the SERVER decides the amount: we post slugs + coupon and
 * the API re-quotes. Whatever this screen displays is cosmetic; the charge is
 * computed again server-side.
 */
export function CheckoutModal({
  quote,
  coupon,
  onClose,
}: {
  quote: Quote;
  coupon: string | null;
  onClose: () => void;
}) {
  const [method, setMethod] = useState<(typeof METHODS)[number]['id']>('card');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { clearCourses } = useCart();
  const navigate = useNavigate();
  const qc = useQueryClient();

  async function pay() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ orderId: string; total: number; items: { slug: string }[] }>('/orders', {
        courseSlugs: quote.items.map((i) => i.slug),
        couponCode: coupon ?? undefined,
        method,
      });
      clearCourses();
      await qc.invalidateQueries({ queryKey: ['course'] });
      await qc.invalidateQueries({ queryKey: ['enrollments'] });
      onClose();
      const first = r.data.items[0]?.slug;
      // completePurchase (:2288): one course opens the player, several go to
      // the dashboard. The dashboard is Phase 5, so多-course lands on the
      // first course's player until it exists.
      if (first) navigate(`/learn/${first}/0/0`);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal onClose={onClose} label="Checkout">
      <h3 style={{ marginBottom: 10 }}>Checkout</h3>

      <div id="checkoutSummary">
        {quote.items.map((i) => (
          <div className="sumRow" key={i.slug}>
            <span>{i.title}</span>
            <span>{formatBDT(i.price)}</span>
          </div>
        ))}
        {quote.bundles.map((b) => (
          <div className="sumRow" style={{ color: 'var(--teal)' }} key={b.slug}>
            <span>
              🧩 {b.title} (−{b.discountPct}%)
            </span>
            <span>−{formatBDT(b.discount)}</span>
          </div>
        ))}
        {quote.couponDiscount > 0 ? (
          <div className="sumRow" style={{ color: 'var(--teal)' }}>
            <span>
              Coupon {quote.couponCode} (−{quote.couponPct}%)
            </span>
            <span>−{formatBDT(quote.couponDiscount)}</span>
          </div>
        ) : null}
        <div className="sumRow total">
          <span>Total</span>
          <span data-testid="checkout-total">{formatBDT(quote.total)}</span>
        </div>
      </div>

      {/* Checkout modal (:602) — "Pay with" is a .chips row and the selected
          method is .chip.active (selectPay :1321 toggles exactly that class).
          These were btn-primary/btn-ghost buttons in a .pay-methods div, which
          is not a class the prototype has and not how the row looks. */}
      <div style={{ margin: '18px 0 6px', fontWeight: 700, fontSize: 14 }}>Pay with</div>
      <div className="chips" id="payChips" style={{ marginBottom: 16 }}>
        {METHODS.map((m) => (
          <button
            key={m.id}
            className={`chip ${method === m.id ? 'active' : ''}`}
            data-testid={`pay-${m.id}`}
            onClick={() => setMethod(m.id)}
          >
            {m.label}
          </button>
        ))}
      </div>

      {error ? (
        <p data-testid="checkout-error" style={{ color: 'var(--coral)', fontSize: 13.5, marginTop: 10 }}>
          {error}
        </p>
      ) : null}

      <button
        className="btn btn-primary btn-block btn-lg"
        style={{ marginTop: 14 }}
        data-testid="checkout-pay"
        disabled={busy}
        onClick={() => void pay()}
      >
        {busy ? 'Processing…' : `Pay ${formatBDT(quote.total)}`}
      </button>
      <p className="asub" style={{ fontSize: 11.5, textAlign: 'center', marginTop: 8 }}>
        No payment provider is connected yet — this records the order without taking money.
      </p>
    </Modal>
  );
}
